import { max, sum } from 'd3-array';
import constant from './constant';
import {
  IDimensions,
  ISankeyGraph,
  ISankeyLink,
  ISankeyNode,
  IPathLink,
  IPathNode,
} from '../interfaces';
import { assertNotNil } from '../../../../utils/types';
import {
  HUNDRED_PERCENT_SIZE,
  NODE_GROUP_VERTICAL_SPACE,
  NODE_HORIZONTAL_SPACE,
} from '../constants';

function value(d: ISankeyNode | ISankeyLink) {
  return d.value;
}

function defaultId(d: ISankeyNode) {
  return d.id;
}

function defaultNodes(sliceNode: IPathNode): ISankeyNode {
  return {
    index: 0,
    sourceLinks: [],
    targetLinks: [],
    value: 0,
    x0: 0,
    x1: 0,
    y0: 0,
    y1: 0,
    nodeGroup: undefined,
    ...sliceNode,
  };
}

function defaultLinks(sliceLink: IPathLink): ISankeyLink {
  return {
    /**
     * Svilen: The following 2 properties are immediately initialized when the
     * ISankeyLinks are processed. There is some effort to clean up D3Sankey
     * public interface to avoid this
     */
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    source: undefined,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    target: undefined,
    width: 0,
    y0: 0,
    y1: 0,
    ...sliceLink,
  };
}

function find(nodeById: Map<string, ISankeyNode>, id: string) {
  const node = nodeById.get(id);
  if (!node) {
    throw new Error('missing: ' + id);
  }
  return node;
}

function computeLinkBreadths({ nodes }: ISankeyGraph) {
  for (const node of nodes) {
    let y0 = node.y0;
    let y1 = y0;
    for (const link of node.sourceLinks) {
      link.y0 = y0 + link.width / 2;
      y0 += link.width;
    }
    for (const link of node.targetLinks) {
      link.y1 = y1 + link.width / 2;
      y1 += link.width;
    }
  }
}

export default function Sankey() {
  let x0 = 0,
    y0 = 0,
    x1 = 1,
    y1 = 1; // extent
  let dx = 24; // nodeWidth
  let dy = 24,
    py: number; // nodePadding
  let id = defaultId;
  let sort: (a: ISankeyNode, b: ISankeyNode) => number;
  let linkSort: (a: ISankeyLink, b: ISankeyLink) => number;
  let nodes: ISankeyNode[] = [];
  let links: ISankeyLink[] = [];

  const size: IDimensions = { width: 0, height: 0 };

  function sankey() {
    const graph = {
      nodes,
      links,
      size,
    };
    computeNodeLinks(graph);
    computeNodeValues(graph);
    // reposition nodes
    computeNodeBreadths(graph);
    // recomputeYBasedOnGroup(graph);
    computeLinkBreadths(graph);
    return graph;
  }

  sankey.update = function (graph: ISankeyGraph) {
    computeLinkBreadths(graph);
    return graph;
  };

  sankey.nodeId = function (_: string | typeof id) {
    return _ !== undefined
      ? ((id = typeof _ === 'function' ? _ : constant(_)), sankey)
      : id;
  };

  sankey.nodeSort = function (_?: typeof sort) {
    return _ !== undefined ? ((sort = _), sankey) : sort;
  };

  sankey.nodeWidth = function (_?: typeof dx) {
    return _ !== undefined ? ((dx = +_), sankey) : dx;
  };

  sankey.nodePadding = function (_?: typeof dy) {
    return _ !== undefined ? ((dy = py = +_), sankey) : dy;
  };

  sankey.nodes = function (_?: IPathNode[]) {
    return _ !== undefined ? ((nodes = _.map(defaultNodes)), sankey) : nodes;
  };

  sankey.links = function (_?: IPathLink[]) {
    return _ !== undefined ? ((links = _.map(defaultLinks)), sankey) : links;
  };

  sankey.linkSort = function (_?: typeof linkSort) {
    return _ !== undefined ? ((linkSort = _), sankey) : linkSort;
  };

  sankey.extent = function (_?: [[number, number], [number, number]]) {
    return _ !== undefined
      ? ((x0 = +_[0][0]),
        (x1 = +_[1][0]),
        (y0 = +_[0][1]),
        (y1 = +_[1][1]),
        sankey)
      : [
          [x0, y0],
          [x1, y1],
        ];
  };

  function computeNodeLinks({ nodes, links }: ISankeyGraph) {
    for (const [i, node] of nodes.entries()) {
      node.index = i;
      node.sourceLinks = [];
      node.targetLinks = [];
    }
    const nodeById = new Map(nodes.map((d) => [id(d), d]));
    for (const [i, link] of links.entries()) {
      link.index = i;
      let { sourceId, targetId, source, target } = link;
      if (typeof source !== 'object') {
        source = link.source = find(nodeById, sourceId);
      }
      if (typeof target !== 'object') {
        target = link.target = find(nodeById, targetId);
      }
      source.sourceLinks.push(link);
      target.targetLinks.push(link);
    }
    if (linkSort != null) {
      for (const { sourceLinks, targetLinks } of nodes) {
        sourceLinks.sort(linkSort);
        targetLinks.sort(linkSort);
      }
    }
  }

  function computeNodeValues({ nodes }: ISankeyGraph) {
    for (const node of nodes) {
      node.value =
        node.fixedValue === undefined
          ? Math.max(sum(node.sourceLinks, value), sum(node.targetLinks, value))
          : node.fixedValue;
    }
  }

  function computeNodeLayers({ nodes }: ISankeyGraph) {
    const columns: ISankeyNode[][] = [];
    for (const node of nodes) {
      const i = node.seq;
      node.x0 = x0 + i * NODE_HORIZONTAL_SPACE;
      node.x1 = node.x0 + dx;
      if (columns[i]) {
        columns[i].push(node);
      } else {
        columns[i] = [node];
      }
      size.width = Math.max(size.width, node.x1);
    }
    if (sort) {
      for (const column of columns) {
        column.sort(sort);
      }
    }
    return columns;
  }

  function padding(column: ISankeyNode[]): number {
    if (column.length === 0) {
      return 0;
    }
    let padding = 0;
    let prev = column[0];
    for (let i = 1; i < column.length; i++) {
      const curr = column[i];
      if (prev.key !== curr.key) {
        padding += py;
      } else {
        padding += NODE_GROUP_VERTICAL_SPACE;
      }
      prev = curr;
    }
    return padding;
  }

  function initializeNodeBreadths(columns: ISankeyNode[][]) {
    const maxSum = max(columns, (c) => sum(c, value)) ?? 0;
    const ky = HUNDRED_PERCENT_SIZE / maxSum;
    const heights = columns.map((c) => sum(c, value) * ky + padding(c));
    const maxHeight = max(heights) ?? 0;
    for (let i = 0; i < columns.length; i++) {
      const nodes = columns[i];
      const height = heights[i];

      let y = y0 + (maxHeight - height) / 2;
      let prevNode: ISankeyNode | undefined;
      let groupNode: ISankeyNode | undefined;
      for (const node of nodes) {
        let padding: number;
        if (prevNode) {
          if (prevNode.key !== node.key) {
            groupNode = node;
            groupNode.nodeGroup = [node];
            padding = py;
          } else {
            padding = NODE_GROUP_VERTICAL_SPACE;
            assertNotNil(groupNode);
            assertNotNil(groupNode.nodeGroup);
            groupNode.nodeGroup.push(node);
          }
        } else {
          padding = 0;
          groupNode = node;
          groupNode.nodeGroup = [node];
        }

        y += padding;

        node.y0 = y;
        node.y1 = y + node.value * ky;

        y = node.y1;

        for (const link of node.sourceLinks) {
          link.width = link.value * ky;
        }
        size.height = Math.max(size.height, node.y1);

        prevNode = node;
      }
    }
  }

  function computeNodeBreadths(graph: ISankeyGraph) {
    const columns = computeNodeLayers(graph);
    initializeNodeBreadths(columns);
  }

  return sankey;
}
